import type { type MtNotificationItemData } from '@presentation/components';
import { MtNotificationsPanel } from '@presentation/components';
import { useCallback, useMemo, useState } from 'react';
import { PAGE_SIZE } from './api/constants';
import type { MtNotificationsGetNotificationsType } from './api/useFormatNotifications';
import { useFormatNotifications } from './api/useFormatNotifications';
import { useGetNotifications } from './api/useGetNotifications';
import { useMarkAsRead } from './api/useMarkAsRead';
import { useNotificationsCount } from './api/useNotificationsCount';

export const MtNotificationsPanelController = () => {
  const { newNotificationsCount } = useNotificationsCount();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [page, setPage] = useState(0);

  const {
    notifications,
    canLoadMore,
    notificationsLoading,
    unreadMessagesCount,
    refetchNotifications,
    loadMoreNotifications,
  } = useGetNotifications();
  const { markAllAsRead, markAsRead } = useMarkAsRead();

  const { formattedNotifications } = useFormatNotifications({
    notifications: notifications as MtNotificationsGetNotificationsType[],
    unreadMessagesCount,
  });

  const unreadNotificationsCount = useMemo(() => {
    if (newNotificationsCount) return newNotificationsCount;
    return notifications?.filter((notif) => notif?.success === false).length;
  }, [newNotificationsCount, notifications]);

  const onBellClickHandler = useCallback(() => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      refetchNotifications((page + 1) * PAGE_SIZE);
    }
  }, [isOpen, page, refetchNotifications]);

  const onClosePanelHandler = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onItemClickHandler = useCallback(
    (notification: MtNotificationItemData) => {
      if (!notification.success) {
        markAsRead(notification.id);
      }
      setIsOpen(false);
    },
    [markAsRead],
  );

  const onMarkAllAsReadClickHandler = useCallback(async () => {
    await markAllAsRead();
    await refetchNotifications(notifications?.length ?? 0);
  }, [markAllAsRead, notifications?.length, refetchNotifications]);

  const onLoadMoreHandler = useCallback(() => {
    loadMoreNotifications((page + 1) * PAGE_SIZE);
    setPage(page + 1);
  }, [loadMoreNotifications, page]);

  return (
    <MtNotificationsPanel
      newNotificationsCount={unreadNotificationsCount}
      isOpen={isOpen}
      onBellClick={onBellClickHandler}
      onClosePanel={onClosePanelHandler}
      items={formattedNotifications}
      onItemClick={onItemClickHandler}
      onMarkAllAsReadClick={onMarkAllAsReadClickHandler}
      onLoadMore={onLoadMoreHandler}
      canLoadMore={canLoadMore}
      isLoading={notificationsLoading}
    />
  );
};
